import { makeAutoObservable, runInAction } from 'mobx';
import { SymbolPriceUpdateInterface } from '@app/common';
import type { RootStore } from '../root-store';
import type { StatisticStore } from './statistic-store';
import { formatApiDate } from '../../functions/format-api-date';
import { TabEnumAllocations } from '../../enums/tab-enum-allocations';
import { isEnumValue } from '../../../utils/typeguards/is-enum-value';
import { UnrealizedAllocationsOverviewMobxDto } from '../../mobx/dtos/statistic/unrealized-allocations-overview-mobx-dto';
import { UnrealizedAllocationMobxDto } from '../../mobx/dtos/statistic/unrealized-allocation-mobx-dto';
import { RealizedAllocationMobxDto } from '../../mobx/dtos/statistic/realized-allocation-mobx-dto';
import { PieChartMobxDto } from '../../mobx/dtos/statistic/pie-chart-mobx-dto';
import { OrderEnum, StatisticControllerGetRatiosRequest } from '../../../defs/api';
import { RealizedAllocationsOverviewMobxDto } from '../../mobx/dtos/statistic/realized-allocations-overview-mobx-dto';

export const UNREALIZED_ALLOCATIONS_LIMIT = 5;
export const REALIZED_ALLOCATIONS_LIMIT = 5;
export const REALIZED_ALLOCATIONS_LIMIT_FREE = 3;

type ProfitOrderField = 'liveProfitValue' | 'liveProfitPercentage' | 'profitValue' | 'profitPercentage';

const PROFIT_COUNTERPARTS: Record<ProfitOrderField, ProfitOrderField> = {
    liveProfitValue: 'liveProfitPercentage',
    liveProfitPercentage: 'liveProfitValue',
    profitValue: 'profitPercentage',
    profitPercentage: 'profitValue',
};

export type SortModel = {
    weight?: OrderEnum | '';
    invested?: OrderEnum | '';
    value?: OrderEnum | '';
    taxFreeValue?: OrderEnum | '';
    currencyImpactValue?: OrderEnum | '';
    liveProfitValue?: OrderEnum | '';
    liveProfitPercentage?: OrderEnum | '';
    profitValue?: OrderEnum | '';
    profitPercentage?: OrderEnum | '';
    dividendsValue?: OrderEnum | '';
};

export type RatiosStoreHydration = {
    symbolTypeRatio: PieChartMobxDto;
    currencyRatio: PieChartMobxDto;
    allocationTypeRatio: PieChartMobxDto;
    allocationTypeRatioFullScreen: PieChartMobxDto | null;
    allAllocations: UnrealizedAllocationMobxDto[];
};

const DEFAULT_SORT_MODEL: SortModel = {
    weight: OrderEnum.Desc,
};

export class RatiosStore {
    rootStore: RootStore;

    statisticStore: StatisticStore;

    symbolTypeRatio: PieChartMobxDto | null = null;

    currencyRatio: PieChartMobxDto | null = null;

    sectorRatio: PieChartMobxDto | null = null;

    sectorRatioFullScreen: PieChartMobxDto | null = null;

    countryRatio: PieChartMobxDto | null = null;

    countryRatioFullScreen: PieChartMobxDto | null = null;

    allocationTypeRatio: PieChartMobxDto | null = null;

    allocationTypeRatioFullScreen: PieChartMobxDto | null = null;

    brokerRatio: PieChartMobxDto | null = null;

    allUnrealizedAllocations: UnrealizedAllocationMobxDto[] = [];

    allRealizedAllocations: RealizedAllocationMobxDto[] = [];

    unrealizedAllocationsOverview: UnrealizedAllocationsOverviewMobxDto | null = null;

    realizedAllocationsOverview: RealizedAllocationsOverviewMobxDto | null = null;

    isUnrealizedAllocationsExpanded: boolean = true;

    isRealizedAllocationsExpanded: boolean = true;

    currentTab: TabEnumAllocations = TabEnumAllocations.Unrealized;

    unrealizedAllocationsProfitOrder: 'liveProfitValue' | 'liveProfitPercentage' = 'liveProfitValue';

    realizedAllocationsProfitOrder: 'profitValue' | 'profitPercentage' = 'profitValue';

    loading = true;

    sortModel: SortModel = {};

    soldMoreThanPurchasedSymbols: string[] = [];

    lastLoadedPortfolioId: string | null = null;

    constructor(statisticStore: StatisticStore) {
        this.statisticStore = statisticStore;
        this.rootStore = statisticStore.rootStore;
        this.sortModel = DEFAULT_SORT_MODEL;

        makeAutoObservable(this);
    }

    get hasRatios() {
        return (
            (this.symbolTypeRatio && this.symbolTypeRatio.data.length > 0) ||
            (this.currencyRatio && this.currencyRatio.data.length > 0) ||
            (this.sectorRatio && this.sectorRatio.data.length > 0) ||
            (this.countryRatio && this.countryRatio.data.length > 0) ||
            (this.allocationTypeRatio && this.allocationTypeRatio.data.length > 0) ||
            (this.brokerRatio && this.brokerRatio.data.length > 0)
        );
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    invalidateFullScreenRatios(): void {
        this.allocationTypeRatioFullScreen = null;
        this.sectorRatioFullScreen = null;
        this.countryRatioFullScreen = null;
    }

    async fetchRatios(includeMinorCategories?: boolean): Promise<void> {
        const portfolioId = this.rootStore.portfolioStore.selectedPortfolio.id;

        const parameters: StatisticControllerGetRatiosRequest = {
            portfolioId,
        };

        if (this.statisticStore.ratiosDateFrom) {
            parameters.dateFrom = formatApiDate(this.statisticStore.ratiosDateFrom);
        }

        if (this.statisticStore.ratiosDateTo) {
            parameters.dateTo = formatApiDate(this.statisticStore.ratiosDateTo);
        }

        if (includeMinorCategories !== undefined) {
            parameters.includeMinorCategories = includeMinorCategories;
        }

        this.startLoading();

        try {
            const fetchedRatios = await this.rootStore.loadWithDelay(() => {
                const isSharedPortfolio = this.rootStore.portfolioStore.sharedPortfolio !== null;
                return isSharedPortfolio
                    ? this.rootStore.apiClient.statisticsController.sharedStatisticControllerGetRatios(parameters)
                    : this.rootStore.apiClient.statisticsController.statisticControllerGetRatios(parameters);
            });

            if (fetchedRatios) {
                runInAction(() => {
                    const isNewLoadedPortfolio = this.lastLoadedPortfolioId !== portfolioId;

                    if (includeMinorCategories) {
                        this.allocationTypeRatioFullScreen = PieChartMobxDto.create(fetchedRatios.allocationTypeRatio);
                        this.sectorRatioFullScreen = PieChartMobxDto.create(fetchedRatios.sectorRatio);
                        this.countryRatioFullScreen = PieChartMobxDto.create(fetchedRatios.countryRatio);
                    } else {
                        if (isNewLoadedPortfolio) {
                            this.invalidateFullScreenRatios();
                        }

                        this.symbolTypeRatio = PieChartMobxDto.create(fetchedRatios.symbolTypeRatio);
                        this.currencyRatio = PieChartMobxDto.create(fetchedRatios.currencyRatio);
                        this.sectorRatio = PieChartMobxDto.create(fetchedRatios.sectorRatio);
                        this.countryRatio = PieChartMobxDto.create(fetchedRatios.countryRatio);
                        this.allocationTypeRatio = PieChartMobxDto.create(fetchedRatios.allocationTypeRatio);
                        this.brokerRatio = PieChartMobxDto.create(fetchedRatios.brokerRatio);

                        this.lastLoadedPortfolioId = portfolioId;
                    }

                    this.allUnrealizedAllocations = UnrealizedAllocationMobxDto.createFromArray(
                        fetchedRatios.allocations,
                    );
                    this.allRealizedAllocations = RealizedAllocationMobxDto.createFromArray(
                        fetchedRatios.realizedAllocations,
                    );

                    this.soldMoreThanPurchasedSymbols = fetchedRatios.soldMoreThanPurchasedSymbols;

                    this.unrealizedAllocationsOverview = UnrealizedAllocationsOverviewMobxDto.create(
                        fetchedRatios.unrealizedAllocationsOverview,
                    );
                    this.realizedAllocationsOverview = RealizedAllocationsOverviewMobxDto.create(
                        fetchedRatios.realizedAllocationsOverview,
                    );
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    toggleAllocationsExpanded(): void {
        if (this.currentTab === TabEnumAllocations.Unrealized) {
            this.isUnrealizedAllocationsExpanded = !this.isUnrealizedAllocationsExpanded;
        }

        if (this.currentTab === TabEnumAllocations.Realized) {
            this.isRealizedAllocationsExpanded = !this.isRealizedAllocationsExpanded;
        }
    }

    get allUnrealizedAllocationsCount(): number {
        return this.allUnrealizedAllocations.length;
    }

    get allRealizedAllocationsCount(): number {
        return this.allRealizedAllocations.length;
    }

    get unrealizedAllocations(): UnrealizedAllocationMobxDto[] {
        return this.sortedUnrealizedAllocations();
    }

    get realizedAllocations(): RealizedAllocationMobxDto[] {
        return this.sortedRealizedAllocations();
    }

    get canDisplayMore(): boolean {
        if (this.currentTab === TabEnumAllocations.Unrealized) {
            return this.allUnrealizedAllocationsCount > UNREALIZED_ALLOCATIONS_LIMIT;
        }

        if (this.currentTab === TabEnumAllocations.Realized) {
            return (
                this.allRealizedAllocationsCount >
                (this.rootStore.authStore.userHasFreeTariff
                    ? REALIZED_ALLOCATIONS_LIMIT_FREE
                    : REALIZED_ALLOCATIONS_LIMIT)
            );
        }
        return false;
    }

    setTab(tab: TabEnumAllocations) {
        this.currentTab = tab;
    }

    setSortModel(sortModel: { field: keyof SortModel; method: OrderEnum | null }): void {
        this.sortModel = {};
        if (sortModel.method) {
            this.sortModel[sortModel.field] = sortModel.method;

            if (sortModel.field === 'liveProfitValue' || sortModel.field === 'liveProfitPercentage') {
                this.initLiveProfitsForAllocations();
            }
        } else {
            this.sortModel = DEFAULT_SORT_MODEL;
        }
    }

    private updateAllocationLiveProfit(
        alloc: UnrealizedAllocationMobxDto,
        priceUpdate?: SymbolPriceUpdateInterface,
    ): void {
        const actualProfit = this.rootStore.symbolPriceUpdateStore.getTodayProfitBySymbolId(
            alloc.symbolId,
            alloc.value,
            alloc.amount,
            alloc.dateTo,
            priceUpdate,
        );

        const liveProfitValue = alloc.profitValue + actualProfit;
        const liveProfitPercentage = (liveProfitValue / alloc.invested) * 100;

        alloc.setLiveProfit(liveProfitValue, liveProfitPercentage);
    }

    private initLiveProfitsForAllocations(): void {
        this.allUnrealizedAllocations.forEach((alloc) => {
            this.updateAllocationLiveProfit(alloc);
        });
    }

    setLiveProfit(symbolId: string, priceUpdate: SymbolPriceUpdateInterface): void {
        const alloc = this.allUnrealizedAllocations.find((a) => a.symbolId === symbolId);
        if (!alloc) {
            return;
        }

        this.updateAllocationLiveProfit(alloc, priceUpdate);
    }

    sortedUnrealizedAllocations(): UnrealizedAllocationMobxDto[] {
        const sortedAllocations = [...this.allUnrealizedAllocations].sort((a, b) => {
            return (
                Object.entries(this.sortModel)
                    .map((sortColumn) => {
                        if (sortColumn[0] in a && isEnumValue(OrderEnum, sortColumn[1])) {
                            if (sortColumn[1] === OrderEnum.Desc) {
                                return a[sortColumn[0] as keyof SortModel] < b[sortColumn[0] as keyof SortModel]
                                    ? 1
                                    : -1;
                            }
                            return a[sortColumn[0] as keyof SortModel] < b[sortColumn[0] as keyof SortModel] ? -1 : 1;
                        }
                        return 1;
                    })
                    .pop() || 1
            );
        });

        return this.isUnrealizedAllocationsExpanded
            ? sortedAllocations.slice(0, UNREALIZED_ALLOCATIONS_LIMIT)
            : sortedAllocations;
    }

    sortedRealizedAllocations(): RealizedAllocationMobxDto[] {
        const sortedAllocations = [...this.allRealizedAllocations].sort((a, b) => {
            return (
                Object.entries(this.sortModel)
                    .map((sortColumn) => {
                        if (sortColumn[0] in a && isEnumValue(OrderEnum, sortColumn[1])) {
                            if (sortColumn[1] === OrderEnum.Desc) {
                                return a[sortColumn[0] as keyof RealizedAllocationMobxDto] <
                                    b[sortColumn[0] as keyof RealizedAllocationMobxDto]
                                    ? 1
                                    : -1;
                            }
                            return a[sortColumn[0] as keyof RealizedAllocationMobxDto] <
                                b[sortColumn[0] as keyof RealizedAllocationMobxDto]
                                ? -1
                                : 1;
                        }
                        return 1;
                    })
                    .pop() || 1
            );
        });

        return this.isRealizedAllocationsExpanded
            ? sortedAllocations.slice(
                  0,
                  this.rootStore.authStore.userHasFreeTariff
                      ? REALIZED_ALLOCATIONS_LIMIT_FREE
                      : REALIZED_ALLOCATIONS_LIMIT,
              )
            : sortedAllocations;
    }

    getFieldSortMethod(field: keyof SortModel): OrderEnum | undefined | '' {
        if (this.sortModel && this.sortModel[field]) {
            return this.sortModel[field];
        }
        return undefined;
    }

    get isExpanded(): boolean {
        if (this.currentTab === TabEnumAllocations.Unrealized) {
            return this.isUnrealizedAllocationsExpanded;
        }

        if (this.currentTab === TabEnumAllocations.Realized) {
            return this.isRealizedAllocationsExpanded;
        }
        return false;
    }

    get isProfitColumnSorted(): boolean {
        const { liveProfitValue, liveProfitPercentage, profitValue, profitPercentage } = this.sortModel;
        return Boolean(liveProfitValue || liveProfitPercentage || profitValue || profitPercentage);
    }

    setProfitOrder(order: ProfitOrderField): void {
        if (order === 'liveProfitValue' || order === 'liveProfitPercentage') {
            this.unrealizedAllocationsProfitOrder = order;
        } else {
            this.realizedAllocationsProfitOrder = order;
        }

        const counterpart = PROFIT_COUNTERPARTS[order];
        const rawMethod = this.sortModel[order] || this.sortModel[counterpart] || '';
        const method = isEnumValue(OrderEnum, rawMethod) ? rawMethod : OrderEnum.Desc;

        this.setSortModel({ field: order, method });
    }

    public get unrealizedTodayProfit() {
        return this.allUnrealizedAllocations.reduce(
            (acc, curr) => {
                const profit = this.rootStore.symbolPriceUpdateStore.getTodayProfitBySymbolId(
                    curr.referenceSymbolId ?? curr.symbolId,
                    curr.value,
                    curr.amount,
                    curr.dateTo,
                );

                const taxFreeProfit = this.rootStore.symbolPriceUpdateStore.getTodayProfitBySymbolId(
                    curr.referenceSymbolId ?? curr.symbolId,
                    curr.taxFreeValue,
                    curr.taxFreeAmount,
                    curr.dateTo,
                );
                acc.totalTodayProfit += profit;
                acc.taxFreeTodayProfit += taxFreeProfit;

                return acc;
            },
            { totalTodayProfit: 0, taxFreeTodayProfit: 0 },
        );
    }
}
